import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { EstimatesResponse } from './contractsMirror.js';
import type { EstimatesSearch } from './searchParams.js';

/**
 * TanStack Query hook for the COARSE estimates endpoint (the strangler-fig +
 * coarse-endpoint proof, CARVE-07). One query key per `{status, show_archived}` —
 * selecting a status pill or toggling archived triggers exactly one refetch of
 * `GET /api/v1/estimates?status=…&show_archived=…`. The SERVER computes the
 * enriched rows + the status-filter counts (single GROUP BY) + the 4-card
 * rollups (21-05); the SPA only renders the returned `EstimatesResponse` (no
 * client re-aggregation, no new N+1 — threat T-21-SEC-11 / CARVE-07).
 *
 * The request goes through `apiFetch` (the only fetch path — `credentials:'include'`
 * + `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is
 * derived server-side from the auth context; the SPA NEVER sends a tenant id param
 * (threat T-21-SEC-11).
 */

/** Build the stable query key for a filter (one cache entry per {status, archived}). */
export function estimatesQueryKey(search: EstimatesSearch): readonly unknown[] {
  return ['estimates', search.status ?? null, search.show_archived ?? false] as const;
}

/** Build the coarse-endpoint request path with the active filters (no tenant param). */
export function estimatesRequestPath(search: EstimatesSearch): string {
  const params = new URLSearchParams();
  if (search.status) params.set('status', search.status);
  if (search.show_archived) params.set('show_archived', '1');
  const qs = params.toString();
  return qs ? `/api/v1/estimates?${qs}` : '/api/v1/estimates';
}

export function useEstimatesQuery(search: EstimatesSearch) {
  return useQuery<EstimatesResponse>({
    queryKey: estimatesQueryKey(search),
    queryFn: () => apiFetchJson<EstimatesResponse>(estimatesRequestPath(search)),
  });
}
